/**
 * EditCamera Component
 *
 * Note: This component has been updated to handle localStorage quota issues.
 * Large camera objects are no longer stored in localStorage to prevent
 * "QuotaExceededError" exceptions. Only essential camera info (id, name, type)
 * is stored using the safeSetItem utility function.
 */

import React from "react";
import { NavBar } from "../../common/NavBar";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import { OrangeTextButton } from "../../common/OrangeTextButton";
import { EditViewSyncButton } from "../../common/EditViewSyncButton";
import { SyncNeededBanner } from "../../common/SyncNeededBanner";
import { useNavigate } from "react-router-dom";
import EditIcon from "@mui/icons-material/Edit";
import { EditCameraTabs } from "./EditCameraTabs";
import { handleEditInfoButtonClick } from "./EditCameraUtils/handleEditInfoButtonClick";
import { EditCameraSaveChangesButton } from "./EditCameraSaveChangesButton";
import { EditCameraCancelButton } from "./EditCameraCancelButton";
import BackdropLoadingScreen from "../../common/BackdropLoadingScreen";
import { AddImmixAlertDialog } from "./AddImmixAlertDialog";
import { AddDiceAlertDialog } from "./AddDiceAlertDialog";
import { AddSureviewAlertDialog } from "./AddSureViewAlertDialog";
import useWindowDimensions from "../../common/useWindowDimensions";
import { EditCameraAlerts } from "./EditCameraAlerts";
import { Dispatcher } from "../Sites/SiteAboutPage/SiteAboutPageUtils/siteAboutInterfaces";
import { getEditCameraData } from "./EditCameraUtils/getEditCameraData";
import { ImmixTestSuccessDialog } from "./ImmixTestSuccessDialog";
import { ImmixTestFailDialog } from "./ImmixTestFailDialog";
import { DiceTestSuccessDialog } from "./DiceTestSuccessDialog";
import { DiceTestFailDialog } from "./DiceTestFailDialog";
import { createIgnoreZonesLinksArray } from "../EditIgnoreZones/EditIgnoreZonesUtils/createIgnoreZonesLinksArray";
import { V1RedirectDialog } from "./V1RedirectDialog";
import { getAllCameras } from "./EditCameraUtils/getAllCameras";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { Link, Tooltip } from "@mui/material";
import { resetState } from "./EditCameraUtils/resetState";
import { AddSentinelAlertDialog } from "./AddSentinelAlertDialog";
import ErrorIcon from "@mui/icons-material/Error";
import { AddBoldAlertDialog } from "./AddBoldAlertDialog";
import { AddPatriotAlertDialog } from "./AddPatriotAlertDialog";
import { AddUSMonitoringAlertDialog } from "./AddUSMonitoringAlertDialog";
import { AddSoftguardAlertDialog } from "./AddSoftguardAlertDialog";
import { AddStreamWebhookDialog } from "./AddStreamWebhookDialog";
import { EditStreamWebhookDialog } from "./EditStreamWebhookDialog";
import { DeleteImmixAlertDialog } from "./DeleteImmixAlertDialog";
import { DeleteDiceAlertDialog } from "./DeleteDiceAlertDialog";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { Button } from "@mui/material";
import { useEditCameraStore } from "./editCameraStore/editCameraStore";
import { DeleteWebhookDialog } from "./DeleteWebhookDialog";
import { NotesDialogEditCamera } from "./NotesDialogEditCamera";
import { DeleteAlertDialog } from "./DeleteAlertDialog";
import { AddSysaidAlertDialog } from "./AddSysaidAlertDialog";
import { ProductActionLogsDialog } from "./ProductActionLogsDialog";
import { ActionLogsText } from "../Sites/SiteAboutPage/SiteAboutPageUtils/actionLogsText";
import { useFeatureFlag } from "../../../hooks/useFeatureFlag";
import { hydrateFlexIgnoreZonesForEditCameraPage } from "./EditCameraUtils/flexIgnoreZonesEditCameraHydration";

declare module "@mui/material/styles" {
  interface Palette {
    neutral: Palette["primary"];
  }
}

const theme = createTheme({
  palette: {
    primary: {
      main: "#FF8400",
    },
  },
});

import {
  getNumbersAfterSites,
  getNumbersAfterEditCamera,
  recordEditCameraOpened,
} from "./EditCameraUtils/urlUtils";
export { getNumbersAfterSites, getNumbersAfterEditCamera };

export const getNumbersAfterAboutSite = (url: string) => {
  const match = url.match(/\/aboutsite\/(\d+)/);
  if (match) {
    return match[1];
  } else {
    return "";
  }
};

export const EditCamera = ({
  permissions,
  navBarCollapsed,
  setNavBarCollapsed,
}: {
  permissions: any;
  navBarCollapsed: boolean;
  setNavBarCollapsed: Dispatcher<boolean>;
}) => {
  const {
    backdropLoadingScreenEditCameraOn,
    deleteWebhookDialogOpen,
    resetEditCameraStore,
    notesDialogEditCameraOpen,
    deleteAlertDialogOpen,
    addSysaidAlertDialogOpen,
    lineCrossingActive,
  } = useEditCameraStore();

  const { isFeatureEnabled } = useFeatureFlag();
  const showFlexIgnoreZonesFlag = isFeatureEnabled("showFlexIgnoreZones", false);

  /** Ref so async getEditCameraData completion reads latest LD value for flex backdrop hold. */
  const holdFlexBackdropAfterLoadRef = React.useRef(false);
  holdFlexBackdropAfterLoadRef.current = showFlexIgnoreZonesFlag;

  const [editCameraInitialFlexHydrationDone, setEditCameraInitialFlexHydrationDone] =
    React.useState(false);
  const [editPageLoadProgressPercent, setEditPageLoadProgressPercent] = React.useState<
    number | undefined
  >(undefined);
  const [editPageLoadProgressMessage, setEditPageLoadProgressMessage] =
    React.useState("Loading camera data...");

  // Ref to track if component is mounted - prevents state updates after unmount
  const isMountedRef = React.useRef(true);

  const [cameraDataLoaded, setCameraDataLoaded] = React.useState(false);
  const [cameraObject, setCameraObject] = React.useState<any>({});
  const [siteInfo, setSiteInfo] = React.useState<any>({});
  const [clickedTab, setClickedTab] = React.useState("one");
  const [editMode, setEditMode] = React.useState(false);
  const [addImmixAlertDialogOpen, setAddImmixAlertDialogOpen] =
    React.useState(false);
  const [addDiceAlertDialogOpen, setAddDiceAlertDialogOpen] =
    React.useState(false);
  const [addSureviewAlertDialogOpen, setAddSureviewAlertDialogOpen] =
    React.useState(false);
  const [addSentinelAlertDialogOpen, setAddSentinelAlertDialogOpen] =
    React.useState(false);
  const [addBoldAlertDialogOpen, setAddBoldAlertDialogOpen] =
    React.useState(false);
  const [addPatriotAlertDialogOpen, setAddPatriotAlertDialogOpen] =
    React.useState(false);
  const [addUSMonitoringAlertDialogOpen, setAddUSMonitoringAlertDialogOpen] =
    React.useState(false);
  const [addSoftguardAlertDialogOpen, setAddSoftguardAlertDialogOpen] =
    React.useState(false);
  const [addStreamWebhookDialogOpen, setAddStreamWebhookDialogOpen] =
    React.useState(false);
  const [editStreamWebhookDialogOpen, setEditStreamWebhookDialogOpen] =
    React.useState(false);
  const [deleteImmixAlertDialogOpen, setDeleteImmixAlertDialogOpen] =
    React.useState(false);
  const [deleteDiceAlertDialogOpen, setDeleteDiceAlertDialogOpen] =
    React.useState(false);
  const [productActionLogsDialogOpen, setProductActionLogsDialogOpen] =
    React.useState(false);

  const { height, width } = useWindowDimensions();

  const [cameraGeneralInfo, setCameraGeneralInfo] = React.useState({});
  const [cameraGeneralInfoBackup, setCameraGeneralInfoBackup] =
    React.useState<any>({});
  const [patchObject, setPatchObject] = React.useState({});
  const [generalInfoChangesMade, setGeneralInfoChangesMade] =
    React.useState(false);
  const [activeChangeMade, setActiveChangeMade] = React.useState(false);
  const [axisCamera, setAxisCamera] = React.useState(false);
  const [mobotixCamera, setMobotixCamera] = React.useState(false);

  const [activeTab, setActiveTab] = React.useState(<></>);

  const [backdropLoadingScreenOn, setBackdropLoadingScreenOn] =
    React.useState(true);

  const [immixAlertsOnCamera, setImmixAlertsOnCamera] = React.useState(false);
  const [immixAlerts, setImmixAlerts] = React.useState<any>([]);

  const [diceAlertsOnCamera, setDiceAlertsOnCamera] = React.useState(false);
  const [diceAlerts, setDiceAlerts] = React.useState<any>([]);

  const [sureViewAlertsOnCamera, setSureViewAlertsOnCamera] =
    React.useState(false);
  const [sureviewAlerts, setSureviewAlerts] = React.useState<any>([]);

  const [sentinelAlertsOnCamera, setSentinelAlertsOnCamera] =
    React.useState(false);
  const [sentinelAlerts, setSentinelAlerts] = React.useState<any>([]);

  const [boldAlertsOnCamera, setBoldAlertsOnCamera] = React.useState(false);
  const [boldAlerts, setBoldAlerts] = React.useState<any>([]);

  const [patriotAlertsOnCamera, setPatriotAlertsOnCamera] =
    React.useState(false);
  const [patriotAlerts, setPatriotAlerts] = React.useState<any>([]);

  const [uSMonitoringAlertsOnCamera, setUSMonitoringAlertsOnCamera] =
    React.useState(false);
  const [uSMonitoringAlerts, setUSMonitoringAlerts] = React.useState<any>([]);

  const [softguardAlertsOnCamera, setSoftguardAlertsOnCamera] =
    React.useState(false);
  const [softguardAlerts, setSoftguardAlerts] = React.useState<any>([]);
  const [evalinkAlertsOnCamera, setEvalinkAlertsOnCamera] =
    React.useState(false);

  const [showStreamParameters, setShowStreamParameters] = React.useState(false);

  const handleSitesBreadcrumbClick = (
    event: React.MouseEvent<HTMLAnchorElement, MouseEvent>
  ) => {
    event.preventDefault();
    localStorage.setItem("level0Click", "false");
    localStorage.setItem("navigatedFromSitesAboutPage", "false");
    navigate("/sites");
  };

  const handleSingularSiteBreadcrumbClick = () => {
    navigate(`/sites/aboutsite/${getNumbersAfterSites(location.href)}`);
    localStorage.setItem("navigatedFromEditCameraPage", "true");
  };

  const [ignoreZonesBreadCrumbs, setIgnoreZonesBreadCrumbs] = React.useState(
    <></>
  );

  const [editCameraAlerts, setEditCameraAlerts] = React.useState({
    triggerMotionSuccess: false,
    triggerMotionFail: false,
  });

  const [recipientsConfiguredData, setRecipientsConfiguredData] =
    React.useState([]);

  const [titlesArray, setTitlesArray] = React.useState([
    "Name",
    "Recipient Type",
    "Host",
    "Port",
    "Email From",
    "Email To",
  ]);

  const [actionRulesData, setActionRulesData] = React.useState([]);

  const [actionRulesTitlesArray, setActionRulesTitlesArray] = React.useState([
    "Name",
    "Host",
    "Port",
    "Email From",
    "Email To",
    "Message",
    "Enabled",
    "Profile",
  ]);

  const [applicationsConfiguredData, setApplicationsConfiguredData] =
    React.useState([]);

  const [timeTablesData, setTimeTablesData] = React.useState([]);

  const [iPNotifyData, setIPNotifyData] = React.useState([]);

  const [actionHandlerData, setActionHandlerData] = React.useState([]);

  const [mobotixFinished, setMobotixFinished] = React.useState({
    timeTable: false,
    iPNotify: false,
    actionHandler: false,
  });

  const [axisFinished, setAxisFinished] = React.useState({
    recipient: false,
    actionRules: false,
    application: false,
  });

  const [streamsObject, setStreamsObject] = React.useState<any>({});

  const [activeProducts, setActiveProducts] = React.useState<any>([]);

  const [sendingImmixTest, setSendingImmixTest] = React.useState(false);

  const [immixTestSuccessDialogOpen, setImmixTestSuccessDialogOpen] =
    React.useState(false);

  const [immixTestFailDialogOpen, setImmixTestFailDialogOpen] =
    React.useState(false);

  const [sendingDiceTest, setSendingDiceTest] = React.useState(false);

  const [diceTestSuccessDialogOpen, setDiceTestSuccessDialogOpen] =
    React.useState(false);

  const [diceTestFailDialogOpen, setDiceTestFailDialogOpen] =
    React.useState(false);

  const [v1RedirectDialogOpen, setv1RedirectDialogOpen] = React.useState(false);

  const [srcImages, setSrcImages] = React.useState({});

  const [cameraInfoRow, setCameraInfoRow] = React.useState<any>("");

  const [cameraWebhookData, setCameraWebhookData] = React.useState("");

  const [webhooksAlertsOnCamera, setWebhooksAlertsOnCamera] =
    React.useState(false);

  const [cameraWebhooksPatchObject, setCameraWebhooksPatchObject] =
    React.useState({});

  const [cameraWebhooksChangesMade, setCameraWebhooksChangesMade] =
    React.useState(false);

  const [streamParametersData, setStreamParametersData] = React.useState<any>(
    {}
  );

  const [streamParametersPatchObject, setStreamParametersPatchObject] =
    React.useState<any>({});

  const [streamParametersChangesMade, setStreamParametersChangesMade] =
    React.useState(false);

  const navigate = useNavigate();

  // const { fetchRecipientsConfiguredData }: any = useAxisTabStore();

  const [productsData, setProductsData] = React.useState([
    {
      name: "Intruder",
      label_name: "intruder",
      open: false,
      metrics: {},
    },
    {
      name: "Intruder Plus",
      label_name: "intruderPlus",
      open: false,
      metrics: {},
    },
    {
      name: "Motion Plus",
      label_name: "motionPlus",
      open: false,
      metrics: {},
    },
    { name: "Gun", label_name: "gun", open: false, metrics: {} },
    {
      name: "Crowd",
      label_name: "crowd",
      open: false,
      metrics: {},
    },
    {
      name: "Loitering",
      label_name: "loitering",
      open: false,
      metrics: {},
    },
    {
      name: "Slip and Fall",
      label_name: "fallPerson",
      open: false,
      metrics: {},
    },
    // {
    //   name: "Slip and Fall (old model)",
    //   label_name: "fall",
    //   open: false,
    //   metrics: {
    //     fall: fallMetrics,
    //   },
    // },
    {
      name: "No Hard Hat",
      label_name: "noHardHat",
      open: false,
      metrics: {},
    },
    // { name: "Mask", label_name: "mask", open: false, metrics: {} },
    {
      name: "Social Distancing",
      label_name: "socialDistancing",
      open: false,
      metrics: {},
    },
    // {
    //   name: "Left Object",
    //   label_name: "leftObject",
    //   open: false,
    //   metrics: {},
    // },
    // {
    //   name: "Package",
    //   label_name: "package",
    //   open: false,
    //   metrics: {},
    // },
    { name: "Fire", label_name: "fire", open: false, metrics: {} },
    {
      name: "Vehicle Logo ID",
      label_name: "vehicleLogoId",
      open: false,
      metrics: {},
    },
    // {
    //   name: "People Flow",
    //   label_name: "peopleFlow",
    //   open: false,
    //   metrics: peopleFlowMetrics,
    // },
    {
      name: "Non-UPS",
      label_name: "nonUps",
      open: false,
      metrics: {},
    },
  ]);

  const [backupProductsData, setBackupProductsData] =
    React.useState(productsData);

  const [allTabsOpen, setAllTabsOpen] = React.useState(false);

  const [allCamerasData, setAllCamerasData] = React.useState<any[]>([]);

  const [cameraIndex, setCameraIndex] = React.useState(0);

  React.useEffect(() => {
    // Reset mounted ref to true on mount
    isMountedRef.current = true;

    resetEditCameraStore();
    if (
      Number(getNumbersAfterSites(location.href)) &&
      Number(getNumbersAfterEditCamera(location.href))
    ) {
      localStorage.setItem("editCamerActiveTab", "one");
      localStorage.setItem(
        "currentCameraId",
        getNumbersAfterEditCamera(location.href)
      );
      getEditCameraData(
        navigate,
        getNumbersAfterSites(location.href),
        getNumbersAfterEditCamera(location.href),
        setSiteInfo,
        setCameraGeneralInfo,
        setCameraObject,
        setCameraDataLoaded,
        setBackdropLoadingScreenOn,
        setImmixAlertsOnCamera,
        setIgnoreZonesBreadCrumbs,
        createIgnoreZonesLinksArray,
        handleSitesBreadcrumbClick,
        handleSingularSiteBreadcrumbClick,
        setSureViewAlertsOnCamera,
        setAxisCamera,
        setMobotixFinished,
        setRecipientsConfiguredData,
        setTitlesArray,
        setActionRulesData,
        setActionRulesTitlesArray,
        setApplicationsConfiguredData,
        mobotixFinished,
        setAxisFinished,
        axisFinished,
        setMobotixCamera,
        setTimeTablesData,
        setIPNotifyData,
        setActionHandlerData,
        setCameraGeneralInfoBackup,
        setImmixAlerts,
        setSureviewAlerts,
        setActiveProducts,
        setActiveTab,
        actionRulesTitlesArray,
        applicationsConfiguredData,
        cameraGeneralInfo,
        actionRulesData,
        setSrcImages,
        permissions,
        setCameraInfoRow,
        width,
        setCameraWebhookData,
        setProductsData,
        setBackupProductsData,
        setSentinelAlertsOnCamera,
        setSentinelAlerts,
        setBoldAlertsOnCamera,
        setBoldAlerts,
        setPatriotAlertsOnCamera,
        setPatriotAlerts,
        setUSMonitoringAlertsOnCamera,
        setUSMonitoringAlerts,
        setWebhooksAlertsOnCamera,
        setSoftguardAlertsOnCamera,
        setSoftguardAlerts,
        setEvalinkAlertsOnCamera,
        setStreamParametersData,
        setShowStreamParameters,
        setEditCameraAlerts,
        setDiceAlertsOnCamera,
        setDiceAlerts,
        () => holdFlexBackdropAfterLoadRef.current,
        isMountedRef
      );
      getAllCameras(
        Number(getNumbersAfterSites(location.href)),
        Number(getNumbersAfterEditCamera(location.href)),
        setAllCamerasData,
        setCameraIndex
      );
    } else {
      navigate("/*");
    }

    // Cleanup function - set mounted to false when component unmounts
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const editInformationText = (
    <>
      <EditIcon fontSize="small" />
      &nbsp;Edit Information
    </>
  );

  const seeAlertText = (
    <>
      <ErrorIcon fontSize="small" />
      &nbsp;See Alerts
    </>
  );

  const [alertsLink, setAlertsLink] = React.useState("");

  const autopatrolSite = siteInfo.integration_type_name === "autopatrol";

  React.useEffect(() => {
    if (siteInfo.breadcrumb_info) {
      setAlertsLink(
        `/alerts?group=${
          siteInfo.breadcrumb_info[0].id
        }&site=${getNumbersAfterSites(
          location.href
        )}&camera=${getNumbersAfterEditCamera(location.href)}`
      );
    }
  }, [siteInfo]);

  React.useEffect(() => {
    if (!cameraDataLoaded || siteInfo?.id == null || siteInfo?.id === "") {
      return;
    }

    const controller = new AbortController();

    void (async () => {
      if (!showFlexIgnoreZonesFlag) {
        if (isMountedRef.current) {
          setBackdropLoadingScreenOn(false);
          setEditCameraInitialFlexHydrationDone(true);
          setEditPageLoadProgressPercent(undefined);
          setEditPageLoadProgressMessage("");
        }
        return;
      }

      if (isMountedRef.current) {
        setEditPageLoadProgressPercent(88);
        setEditPageLoadProgressMessage("Loading flex schedule presets...");
      }

      try {
        const focusCameraId = Number(getNumbersAfterEditCamera(location.href));
        await hydrateFlexIgnoreZonesForEditCameraPage(siteInfo.id, {
          signal: controller.signal,
          ...(Number.isFinite(focusCameraId) && focusCameraId > 0
            ? { focusCameraId }
            : {}),
        });
      } catch {
        // Best-effort: flex row still falls back to camera ignore zones API.
      } finally {
        if (isMountedRef.current) {
          setEditPageLoadProgressPercent(undefined);
          setEditPageLoadProgressMessage("");
          setBackdropLoadingScreenOn(false);
          setEditCameraInitialFlexHydrationDone(true);
        }
      }
    })();

    return () => controller.abort();
  }, [cameraDataLoaded, siteInfo?.id, showFlexIgnoreZonesFlag]);

  return (
    <div>
      <SyncNeededBanner navigate={navigate} />
      {notesDialogEditCameraOpen && <NotesDialogEditCamera />}
      {v1RedirectDialogOpen && (
        <V1RedirectDialog
          v1RedirectDialogOpen={v1RedirectDialogOpen}
          setv1RedirectDialogOpen={setv1RedirectDialogOpen}
        />
      )}
      {addImmixAlertDialogOpen && (
        <AddImmixAlertDialog
          setAddImmixAlertDialogOpen={setAddImmixAlertDialogOpen}
          addImmixAlertDialogOpen={addImmixAlertDialogOpen}
          immixAlerts={immixAlerts}
          setBackdropLoadingScreenOn={setBackdropLoadingScreenOn}
          setCameraGeneralInfo={setCameraGeneralInfo}
          setCameraGeneralInfoBackup={setCameraGeneralInfoBackup}
          setGeneralInfoChangesMade={setGeneralInfoChangesMade}
          setPatchObject={setPatchObject}
          setImmixAlerts={setImmixAlerts}
          setActiveTab={setActiveTab}
          cameraGeneralInfoBackup={cameraGeneralInfoBackup}
          setIgnoreZonesBreadCrumbs={setIgnoreZonesBreadCrumbs}
          handleSitesBreadcrumbClick={handleSitesBreadcrumbClick}
          handleSingularSiteBreadcrumbClick={handleSingularSiteBreadcrumbClick}
          cameraGeneralInfo={cameraGeneralInfo}
          setSendingImmixTest={setSendingImmixTest}
          setImmixTestSuccessDialogOpen={setImmixTestSuccessDialogOpen}
          setImmixTestFailDialogOpen={setImmixTestFailDialogOpen}
          setDeleteImmixAlertDialogOpen={setDeleteImmixAlertDialogOpen}
          sureviewAlerts={sureviewAlerts}
          setAddSureviewAlertDialogOpen={setAddSureviewAlertDialogOpen}
          cameraWebhookData={cameraWebhookData}
          setAddStreamWebhookDialogOpen={setAddStreamWebhookDialogOpen}
          setEditStreamWebhookDialogOpen={setEditStreamWebhookDialogOpen}
          sentinelAlerts={sentinelAlerts}
          setAddSentinelAlertDialogOpen={setAddSentinelAlertDialogOpen}
          boldAlerts={boldAlerts}
          setAddBoldAlertDialogOpen={setAddBoldAlertDialogOpen}
          patriotAlerts={patriotAlerts}
          setAddPatriotAlertDialogOpen={setAddPatriotAlertDialogOpen}
          softguardAlerts={softguardAlerts}
          setAddSoftguardAlertDialogOpen={setAddSoftguardAlertDialogOpen}
          immixAlertsOnCamera={immixAlertsOnCamera}
          sureViewAlertsOnCamera={sureViewAlertsOnCamera}
          siteInfo={siteInfo}
          webhooksAlertsOnCamera={webhooksAlertsOnCamera}
          sentinelAlertsOnCamera={sentinelAlertsOnCamera}
          boldAlertsOnCamera={boldAlertsOnCamera}
          patriotAlertsOnCamera={patriotAlertsOnCamera}
          softguardAlertsOnCamera={softguardAlertsOnCamera}
          evalinkAlertsOnCamera={evalinkAlertsOnCamera}
        />
      )}
      {addDiceAlertDialogOpen && (
        <AddDiceAlertDialog
          setAddDiceAlertDialogOpen={setAddDiceAlertDialogOpen}
          addDiceAlertDialogOpen={addDiceAlertDialogOpen}
          diceAlerts={diceAlerts}
          setBackdropLoadingScreenOn={setBackdropLoadingScreenOn}
          setCameraGeneralInfo={setCameraGeneralInfo}
          setCameraGeneralInfoBackup={setCameraGeneralInfoBackup}
          setGeneralInfoChangesMade={setGeneralInfoChangesMade}
          setPatchObject={setPatchObject}
          setDiceAlerts={setDiceAlerts}
          setActiveTab={setActiveTab}
          cameraGeneralInfoBackup={cameraGeneralInfoBackup}
          setIgnoreZonesBreadCrumbs={setIgnoreZonesBreadCrumbs}
          handleSitesBreadcrumbClick={handleSitesBreadcrumbClick}
          handleSingularSiteBreadcrumbClick={handleSingularSiteBreadcrumbClick}
          cameraGeneralInfo={cameraGeneralInfo}
          setSendingDiceTest={setSendingDiceTest}
          setDiceTestSuccessDialogOpen={setDiceTestSuccessDialogOpen}
          setDiceTestFailDialogOpen={setDiceTestFailDialogOpen}
          setDeleteDiceAlertDialogOpen={setDeleteDiceAlertDialogOpen}
          sureviewAlerts={sureviewAlerts}
          setAddSureviewAlertDialogOpen={setAddSureviewAlertDialogOpen}
          cameraWebhookData={cameraWebhookData}
          setAddStreamWebhookDialogOpen={setAddStreamWebhookDialogOpen}
          setEditStreamWebhookDialogOpen={setEditStreamWebhookDialogOpen}
          sentinelAlerts={sentinelAlerts}
          setAddSentinelAlertDialogOpen={setAddSentinelAlertDialogOpen}
          boldAlerts={boldAlerts}
          setAddBoldAlertDialogOpen={setAddBoldAlertDialogOpen}
          patriotAlerts={patriotAlerts}
          setAddPatriotAlertDialogOpen={setAddPatriotAlertDialogOpen}
          softguardAlerts={softguardAlerts}
          setAddSoftguardAlertDialogOpen={setAddSoftguardAlertDialogOpen}
          diceAlertsOnCamera={diceAlertsOnCamera}
          sureViewAlertsOnCamera={sureViewAlertsOnCamera}
          siteInfo={siteInfo}
          webhooksAlertsOnCamera={webhooksAlertsOnCamera}
          sentinelAlertsOnCamera={sentinelAlertsOnCamera}
          boldAlertsOnCamera={boldAlertsOnCamera}
          patriotAlertsOnCamera={patriotAlertsOnCamera}
          softguardAlertsOnCamera={softguardAlertsOnCamera}
          evalinkAlertsOnCamera={evalinkAlertsOnCamera}
        />
      )}
      {deleteImmixAlertDialogOpen && (
        <DeleteImmixAlertDialog
          deleteImmixAlertDialogOpen={deleteImmixAlertDialogOpen}
          setDeleteImmixAlertDialogOpen={setDeleteImmixAlertDialogOpen}
          setBackdropLoadingScreenOn={setBackdropLoadingScreenOn}
          immixAlerts={immixAlerts}
          setImmixAlerts={setImmixAlerts}
          setActiveTab={setActiveTab}
          setAddImmixAlertDialogOpen={setAddImmixAlertDialogOpen}
          setSendingImmixTest={setSendingImmixTest}
          setImmixTestSuccessDialogOpen={setImmixTestSuccessDialogOpen}
          setImmixTestFailDialogOpen={setImmixTestFailDialogOpen}
          sureviewAlerts={sureviewAlerts}
          setAddSureviewAlertDialogOpen={setAddSureviewAlertDialogOpen}
          cameraWebhookData={cameraWebhookData}
          setAddStreamWebhookDialogOpen={setAddStreamWebhookDialogOpen}
          setEditStreamWebhookDialogOpen={setEditStreamWebhookDialogOpen}
          sentinelAlerts={sentinelAlerts}
          setAddSentinelAlertDialogOpen={setAddSentinelAlertDialogOpen}
          boldAlerts={boldAlerts}
          setAddBoldAlertDialogOpen={setAddBoldAlertDialogOpen}
          patriotAlerts={patriotAlerts}
          setAddPatriotAlertDialogOpen={setAddPatriotAlertDialogOpen}
          softguardAlerts={softguardAlerts}
          setAddSoftguardAlertDialogOpen={setAddSoftguardAlertDialogOpen}
          immixAlertsOnCamera={immixAlertsOnCamera}
          sureViewAlertsOnCamera={sureViewAlertsOnCamera}
          siteInfo={siteInfo}
          webhooksAlertsOnCamera={webhooksAlertsOnCamera}
          sentinelAlertsOnCamera={sentinelAlertsOnCamera}
          boldAlertsOnCamera={boldAlertsOnCamera}
          patriotAlertsOnCamera={patriotAlertsOnCamera}
          softguardAlertsOnCamera={softguardAlertsOnCamera}
          evalinkAlertsOnCamera={evalinkAlertsOnCamera}
          navigate={navigate}
        />
      )}
      {deleteDiceAlertDialogOpen && (
        <DeleteDiceAlertDialog
          deleteDiceAlertDialogOpen={deleteDiceAlertDialogOpen}
          setDeleteDiceAlertDialogOpen={setDeleteDiceAlertDialogOpen}
          setBackdropLoadingScreenOn={setBackdropLoadingScreenOn}
          diceAlerts={diceAlerts}
          setDiceAlerts={setDiceAlerts}
          setActiveTab={setActiveTab}
          setAddDiceAlertDialogOpen={setAddDiceAlertDialogOpen}
          setSendingDiceTest={setSendingDiceTest}
          setDiceTestSuccessDialogOpen={setDiceTestSuccessDialogOpen}
          setDiceTestFailDialogOpen={setDiceTestFailDialogOpen}
          sureviewAlerts={sureviewAlerts}
          setAddSureviewAlertDialogOpen={setAddSureviewAlertDialogOpen}
          cameraWebhookData={cameraWebhookData}
          setAddStreamWebhookDialogOpen={setAddStreamWebhookDialogOpen}
          setEditStreamWebhookDialogOpen={setEditStreamWebhookDialogOpen}
          sentinelAlerts={sentinelAlerts}
          setAddSentinelAlertDialogOpen={setAddSentinelAlertDialogOpen}
          boldAlerts={boldAlerts}
          setAddBoldAlertDialogOpen={setAddBoldAlertDialogOpen}
          patriotAlerts={patriotAlerts}
          setAddPatriotAlertDialogOpen={setAddPatriotAlertDialogOpen}
          softguardAlerts={softguardAlerts}
          setAddSoftguardAlertDialogOpen={setAddSoftguardAlertDialogOpen}
          diceAlertsOnCamera={diceAlertsOnCamera}
          sureViewAlertsOnCamera={sureViewAlertsOnCamera}
          siteInfo={siteInfo}
          webhooksAlertsOnCamera={webhooksAlertsOnCamera}
          sentinelAlertsOnCamera={sentinelAlertsOnCamera}
          boldAlertsOnCamera={boldAlertsOnCamera}
          patriotAlertsOnCamera={patriotAlertsOnCamera}
          softguardAlertsOnCamera={softguardAlertsOnCamera}
          evalinkAlertsOnCamera={evalinkAlertsOnCamera}
          navigate={navigate}
        />
      )}
      {deleteAlertDialogOpen && <DeleteAlertDialog />}
      {addSentinelAlertDialogOpen && (
        <AddSentinelAlertDialog
          setAddSentinelAlertDialogOpen={setAddSentinelAlertDialogOpen}
          addSentinelAlertDialogOpen={addSentinelAlertDialogOpen}
          sentinelAlerts={sentinelAlerts}
          setBackdropLoadingScreenOn={setBackdropLoadingScreenOn}
          setCameraGeneralInfo={setCameraGeneralInfo}
          setCameraGeneralInfoBackup={setCameraGeneralInfoBackup}
          setGeneralInfoChangesMade={setGeneralInfoChangesMade}
          setPatchObject={setPatchObject}
          setSentinelAlerts={setSentinelAlerts}
          setActiveTab={setActiveTab}
          cameraGeneralInfoBackup={cameraGeneralInfoBackup}
          setIgnoreZonesBreadCrumbs={setIgnoreZonesBreadCrumbs}
          handleSitesBreadcrumbClick={handleSitesBreadcrumbClick}
          handleSingularSiteBreadcrumbClick={handleSingularSiteBreadcrumbClick}
          cameraGeneralInfo={cameraGeneralInfo}
          immixAlerts={immixAlerts}
          setAddImmixAlertDialogOpen={setAddImmixAlertDialogOpen}
          setSendingImmixTest={setSendingImmixTest}
          setImmixTestSuccessDialogOpen={setImmixTestSuccessDialogOpen}
          setImmixTestFailDialogOpen={setImmixTestFailDialogOpen}
          setDeleteImmixAlertDialogOpen={setDeleteImmixAlertDialogOpen}
          sureviewAlerts={sureviewAlerts}
          setAddSureviewAlertDialogOpen={setAddSureviewAlertDialogOpen}
          cameraWebhookData={cameraWebhookData}
          setAddStreamWebhookDialogOpen={setAddStreamWebhookDialogOpen}
          setEditStreamWebhookDialogOpen={setEditStreamWebhookDialogOpen}
          boldAlerts={boldAlerts}
          setAddBoldAlertDialogOpen={setAddBoldAlertDialogOpen}
          patriotAlerts={patriotAlerts}
          setAddPatriotAlertDialogOpen={setAddPatriotAlertDialogOpen}
          softguardAlerts={softguardAlerts}
          setAddSoftguardAlertDialogOpen={setAddSoftguardAlertDialogOpen}
          immixAlertsOnCamera={immixAlertsOnCamera}
          sureViewAlertsOnCamera={sureViewAlertsOnCamera}
          siteInfo={siteInfo}
          webhooksAlertsOnCamera={webhooksAlertsOnCamera}
          sentinelAlertsOnCamera={sentinelAlertsOnCamera}
          boldAlertsOnCamera={boldAlertsOnCamera}
          patriotAlertsOnCamera={patriotAlertsOnCamera}
          softguardAlertsOnCamera={softguardAlertsOnCamera}
          evalinkAlertsOnCamera={evalinkAlertsOnCamera}
        />
      )}
      {addBoldAlertDialogOpen && (
        <AddBoldAlertDialog
          setAddBoldAlertDialogOpen={setAddBoldAlertDialogOpen}
          addBoldAlertDialogOpen={addBoldAlertDialogOpen}
          boldAlerts={boldAlerts}
          setBackdropLoadingScreenOn={setBackdropLoadingScreenOn}
          setCameraGeneralInfo={setCameraGeneralInfo}
          setCameraGeneralInfoBackup={setCameraGeneralInfoBackup}
          setGeneralInfoChangesMade={setGeneralInfoChangesMade}
          setPatchObject={setPatchObject}
          setBoldAlerts={setBoldAlerts}
          setActiveTab={setActiveTab}
          cameraGeneralInfoBackup={cameraGeneralInfoBackup}
          setIgnoreZonesBreadCrumbs={setIgnoreZonesBreadCrumbs}
          handleSitesBreadcrumbClick={handleSitesBreadcrumbClick}
          handleSingularSiteBreadcrumbClick={handleSingularSiteBreadcrumbClick}
          cameraGeneralInfo={cameraGeneralInfo}
          immixAlerts={immixAlerts}
          setAddImmixAlertDialogOpen={setAddImmixAlertDialogOpen}
          setSendingImmixTest={setSendingImmixTest}
          setImmixTestSuccessDialogOpen={setImmixTestSuccessDialogOpen}
          setImmixTestFailDialogOpen={setImmixTestFailDialogOpen}
          setDeleteImmixAlertDialogOpen={setDeleteImmixAlertDialogOpen}
          sureviewAlerts={sureviewAlerts}
          setAddSureviewAlertDialogOpen={setAddSureviewAlertDialogOpen}
          cameraWebhookData={cameraWebhookData}
          setAddStreamWebhookDialogOpen={setAddStreamWebhookDialogOpen}
          setEditStreamWebhookDialogOpen={setEditStreamWebhookDialogOpen}
          sentinelAlerts={sentinelAlerts}
          setAddSentinelAlertDialogOpen={setAddSentinelAlertDialogOpen}
          patriotAlerts={patriotAlerts}
          setAddPatriotAlertDialogOpen={setAddPatriotAlertDialogOpen}
          softguardAlerts={softguardAlerts}
          setAddSoftguardAlertDialogOpen={setAddSoftguardAlertDialogOpen}
          immixAlertsOnCamera={immixAlertsOnCamera}
          sureViewAlertsOnCamera={sureViewAlertsOnCamera}
          siteInfo={siteInfo}
          webhooksAlertsOnCamera={webhooksAlertsOnCamera}
          sentinelAlertsOnCamera={sentinelAlertsOnCamera}
          boldAlertsOnCamera={boldAlertsOnCamera}
          patriotAlertsOnCamera={patriotAlertsOnCamera}
          softguardAlertsOnCamera={softguardAlertsOnCamera}
          evalinkAlertsOnCamera={evalinkAlertsOnCamera}
        />
      )}
      {addPatriotAlertDialogOpen && (
        <AddPatriotAlertDialog
          setAddPatriotAlertDialogOpen={setAddPatriotAlertDialogOpen}
          addPatriotAlertDialogOpen={addPatriotAlertDialogOpen}
          patriotAlerts={patriotAlerts}
          setBackdropLoadingScreenOn={setBackdropLoadingScreenOn}
          setCameraGeneralInfo={setCameraGeneralInfo}
          setCameraGeneralInfoBackup={setCameraGeneralInfoBackup}
          setGeneralInfoChangesMade={setGeneralInfoChangesMade}
          setPatchObject={setPatchObject}
          setPatriotAlerts={setPatriotAlerts}
          setActiveTab={setActiveTab}
          cameraGeneralInfoBackup={cameraGeneralInfoBackup}
          setIgnoreZonesBreadCrumbs={setIgnoreZonesBreadCrumbs}
          handleSitesBreadcrumbClick={handleSitesBreadcrumbClick}
          handleSingularSiteBreadcrumbClick={handleSingularSiteBreadcrumbClick}
          cameraGeneralInfo={cameraGeneralInfo}
          immixAlerts={immixAlerts}
          setAddImmixAlertDialogOpen={setAddImmixAlertDialogOpen}
          setSendingImmixTest={setSendingImmixTest}
          setImmixTestSuccessDialogOpen={setImmixTestSuccessDialogOpen}
          setImmixTestFailDialogOpen={setImmixTestFailDialogOpen}
          setDeleteImmixAlertDialogOpen={setDeleteImmixAlertDialogOpen}
          sureviewAlerts={sureviewAlerts}
          setAddSureviewAlertDialogOpen={setAddSureviewAlertDialogOpen}
          cameraWebhookData={cameraWebhookData}
          setAddStreamWebhookDialogOpen={setAddStreamWebhookDialogOpen}
          setEditStreamWebhookDialogOpen={setEditStreamWebhookDialogOpen}
          sentinelAlerts={sentinelAlerts}
          setAddSentinelAlertDialogOpen={setAddSentinelAlertDialogOpen}
          boldAlerts={boldAlerts}
          setAddBoldAlertDialogOpen={setAddBoldAlertDialogOpen}
          softguardAlerts={softguardAlerts}
          setAddSoftguardAlertDialogOpen={setAddSoftguardAlertDialogOpen}
          immixAlertsOnCamera={immixAlertsOnCamera}
          sureViewAlertsOnCamera={sureViewAlertsOnCamera}
          siteInfo={siteInfo}
          webhooksAlertsOnCamera={webhooksAlertsOnCamera}
          sentinelAlertsOnCamera={sentinelAlertsOnCamera}
          boldAlertsOnCamera={boldAlertsOnCamera}
          patriotAlertsOnCamera={patriotAlertsOnCamera}
          softguardAlertsOnCamera={softguardAlertsOnCamera}
          evalinkAlertsOnCamera={evalinkAlertsOnCamera}
        />
      )}
      {addUSMonitoringAlertDialogOpen && (
        <AddUSMonitoringAlertDialog
          setAddUSMonitoringAlertDialogOpen={setAddUSMonitoringAlertDialogOpen}
          addUSMonitoringAlertDialogOpen={addUSMonitoringAlertDialogOpen}
          uSMonitoringAlerts={uSMonitoringAlerts}
          setBackdropLoadingScreenOn={setBackdropLoadingScreenOn}
          setCameraGeneralInfo={setCameraGeneralInfo}
          setCameraGeneralInfoBackup={setCameraGeneralInfoBackup}
          setGeneralInfoChangesMade={setGeneralInfoChangesMade}
          setPatchObject={setPatchObject}
          setUSMonitoringAlerts={setUSMonitoringAlerts}
          setActiveTab={setActiveTab}
          cameraGeneralInfoBackup={cameraGeneralInfoBackup}
          setIgnoreZonesBreadCrumbs={setIgnoreZonesBreadCrumbs}
          handleSitesBreadcrumbClick={handleSitesBreadcrumbClick}
          handleSingularSiteBreadcrumbClick={handleSingularSiteBreadcrumbClick}
          cameraGeneralInfo={cameraGeneralInfo}
        />
      )}
      {addSoftguardAlertDialogOpen && (
        <AddSoftguardAlertDialog
          setAddSoftguardAlertDialogOpen={setAddSoftguardAlertDialogOpen}
          addSoftguardAlertDialogOpen={addSoftguardAlertDialogOpen}
          softguardAlerts={softguardAlerts}
          setBackdropLoadingScreenOn={setBackdropLoadingScreenOn}
          setCameraGeneralInfo={setCameraGeneralInfo}
          setCameraGeneralInfoBackup={setCameraGeneralInfoBackup}
          setGeneralInfoChangesMade={setGeneralInfoChangesMade}
          setPatchObject={setPatchObject}
          setSoftguardAlerts={setSoftguardAlerts}
          setActiveTab={setActiveTab}
          cameraGeneralInfoBackup={cameraGeneralInfoBackup}
          setIgnoreZonesBreadCrumbs={setIgnoreZonesBreadCrumbs}
          handleSitesBreadcrumbClick={handleSitesBreadcrumbClick}
          handleSingularSiteBreadcrumbClick={handleSingularSiteBreadcrumbClick}
          cameraGeneralInfo={cameraGeneralInfo}
          immixAlerts={immixAlerts}
          setAddImmixAlertDialogOpen={setAddImmixAlertDialogOpen}
          setSendingImmixTest={setSendingImmixTest}
          setImmixTestSuccessDialogOpen={setImmixTestSuccessDialogOpen}
          setImmixTestFailDialogOpen={setImmixTestFailDialogOpen}
          setDeleteImmixAlertDialogOpen={setDeleteImmixAlertDialogOpen}
          sureviewAlerts={sureviewAlerts}
          setAddSureviewAlertDialogOpen={setAddSureviewAlertDialogOpen}
          cameraWebhookData={cameraWebhookData}
          setAddStreamWebhookDialogOpen={setAddStreamWebhookDialogOpen}
          setEditStreamWebhookDialogOpen={setEditStreamWebhookDialogOpen}
          sentinelAlerts={sentinelAlerts}
          setAddSentinelAlertDialogOpen={setAddSentinelAlertDialogOpen}
          patriotAlerts={patriotAlerts}
          setAddPatriotAlertDialogOpen={setAddPatriotAlertDialogOpen}
          boldAlerts={boldAlerts}
          setAddBoldAlertDialogOpen={setAddBoldAlertDialogOpen}
          immixAlertsOnCamera={immixAlertsOnCamera}
          sureViewAlertsOnCamera={sureViewAlertsOnCamera}
          siteInfo={siteInfo}
          webhooksAlertsOnCamera={webhooksAlertsOnCamera}
          sentinelAlertsOnCamera={sentinelAlertsOnCamera}
          boldAlertsOnCamera={boldAlertsOnCamera}
          patriotAlertsOnCamera={patriotAlertsOnCamera}
          softguardAlertsOnCamera={softguardAlertsOnCamera}
          evalinkAlertsOnCamera={evalinkAlertsOnCamera}
        />
      )}
      {addSysaidAlertDialogOpen && (
        <AddSysaidAlertDialog
          setCameraGeneralInfo={setCameraGeneralInfo}
          setCameraGeneralInfoBackup={setCameraGeneralInfoBackup}
          setPatchObject={setPatchObject}
          cameraGeneralInfoBackup={cameraGeneralInfoBackup}
        />
      )}
      {addStreamWebhookDialogOpen && (
        <AddStreamWebhookDialog
          setAddStreamWebhookDialogOpen={setAddStreamWebhookDialogOpen}
          addStreamWebhookDialogOpen={addStreamWebhookDialogOpen}
          cameraGeneralInfo={cameraGeneralInfo}
        />
      )}
      {editStreamWebhookDialogOpen && (
        <EditStreamWebhookDialog
          setEditStreamWebhookDialogOpen={setEditStreamWebhookDialogOpen}
          editStreamWebhookDialogOpen={editStreamWebhookDialogOpen}
        />
      )}
      {immixTestSuccessDialogOpen && (
        <ImmixTestSuccessDialog
          immixTestSuccessDialogOpen={immixTestSuccessDialogOpen}
          setImmixTestSuccessDialogOpen={setImmixTestSuccessDialogOpen}
        />
      )}
      {immixTestFailDialogOpen && (
        <ImmixTestFailDialog
          immixTestFailDialogOpen={immixTestFailDialogOpen}
          setImmixTestFailDialogOpen={setImmixTestFailDialogOpen}
        />
      )}
      {diceTestSuccessDialogOpen && (
        <DiceTestSuccessDialog
          diceTestSuccessDialogOpen={diceTestSuccessDialogOpen}
          setDiceTestSuccessDialogOpen={setDiceTestSuccessDialogOpen}
        />
      )}
      {diceTestFailDialogOpen && (
        <DiceTestFailDialog
          diceTestFailDialogOpen={diceTestFailDialogOpen}
          setDiceTestFailDialogOpen={setDiceTestFailDialogOpen}
        />
      )}
      {addSureviewAlertDialogOpen && (
        <AddSureviewAlertDialog
          setAddSureviewAlertDialogOpen={setAddSureviewAlertDialogOpen}
          addSureviewAlertDialogOpen={addSureviewAlertDialogOpen}
          sureViewAlerts={sureviewAlerts}
          setBackdropLoadingScreenOn={setBackdropLoadingScreenOn}
          setCameraGeneralInfo={setCameraGeneralInfo}
          setCameraGeneralInfoBackup={setCameraGeneralInfoBackup}
          setGeneralInfoChangesMade={setGeneralInfoChangesMade}
          setPatchObject={setPatchObject}
          setSureviewAlerts={setSureviewAlerts}
          setActiveTab={setActiveTab}
          cameraGeneralInfoBackup={cameraGeneralInfoBackup}
          setIgnoreZonesBreadCrumbs={setIgnoreZonesBreadCrumbs}
          handleSitesBreadcrumbClick={handleSitesBreadcrumbClick}
          handleSingularSiteBreadcrumbClick={handleSingularSiteBreadcrumbClick}
          cameraGeneralInfo={cameraGeneralInfo}
          immixAlerts={immixAlerts}
          setAddImmixAlertDialogOpen={setAddImmixAlertDialogOpen}
          setSendingImmixTest={setSendingImmixTest}
          setImmixTestSuccessDialogOpen={setImmixTestSuccessDialogOpen}
          setImmixTestFailDialogOpen={setImmixTestFailDialogOpen}
          setDeleteImmixAlertDialogOpen={setDeleteImmixAlertDialogOpen}
          cameraWebhookData={cameraWebhookData}
          setAddStreamWebhookDialogOpen={setAddStreamWebhookDialogOpen}
          setEditStreamWebhookDialogOpen={setEditStreamWebhookDialogOpen}
          sentinelAlerts={sentinelAlerts}
          setAddSentinelAlertDialogOpen={setAddSentinelAlertDialogOpen}
          boldAlerts={boldAlerts}
          setAddBoldAlertDialogOpen={setAddBoldAlertDialogOpen}
          patriotAlerts={patriotAlerts}
          setAddPatriotAlertDialogOpen={setAddPatriotAlertDialogOpen}
          softguardAlerts={softguardAlerts}
          setAddSoftguardAlertDialogOpen={setAddSoftguardAlertDialogOpen}
          immixAlertsOnCamera={immixAlertsOnCamera}
          sureViewAlertsOnCamera={sureViewAlertsOnCamera}
          siteInfo={siteInfo}
          webhooksAlertsOnCamera={webhooksAlertsOnCamera}
          sentinelAlertsOnCamera={sentinelAlertsOnCamera}
          boldAlertsOnCamera={boldAlertsOnCamera}
          patriotAlertsOnCamera={patriotAlertsOnCamera}
          softguardAlertsOnCamera={softguardAlertsOnCamera}
          evalinkAlertsOnCamera={evalinkAlertsOnCamera}
        />
      )}
      {deleteWebhookDialogOpen && <DeleteWebhookDialog navigate={navigate} />}
      <ProductActionLogsDialog
        open={productActionLogsDialogOpen}
        onClose={() => setProductActionLogsDialogOpen(false)}
        cameraId={getNumbersAfterEditCamera(location.href)}
        siteTimezone={siteInfo?.timezone ?? null}
      />
      <div>
        <BackdropLoadingScreen
          openState={backdropLoadingScreenOn}
          progressPercent={editPageLoadProgressPercent}
          message={editPageLoadProgressMessage}
        />
        <BackdropLoadingScreen openState={sendingImmixTest} />
        <BackdropLoadingScreen openState={sendingDiceTest} />
        <BackdropLoadingScreen openState={backdropLoadingScreenEditCameraOn} />
        <EditCameraAlerts
          editCameraAlerts={editCameraAlerts}
          setEditCameraAlerts={setEditCameraAlerts}
          cameraName={cameraObject.camera_name}
        />
        {cameraDataLoaded &&
          cameraInfoRow &&
          (!showFlexIgnoreZonesFlag || editCameraInitialFlexHydrationDone) && (
          <div className="bg-actuate-grey absolute w-full h-full overflow-scroll">
            <div
              className={
                !navBarCollapsed
                  ? "ml-[150px] mr-[1px] min-w-[1250px] w-[calc(100%-151px)]"
                  : "ml-[75px] mr-[1px] min-w-[1250px] w-[calc(100%-76px)]"
              }
            >
            <div className="relative mt-[15px] h-[59px] w-full min-w-[1250px] bg-white">
              <div className="absolute top-[7px] left-0 right-0 flex min-w-[1250px] items-center">
                <div className="min-w-0 flex-1 overflow-hidden">
                  {ignoreZonesBreadCrumbs}
                </div>
                <div className="ml-4 mr-[7vw] shrink-0 flex items-center gap-3">
                  <EditViewSyncButton navigate={navigate} />
                  <OrangeTextButton
                    onClick={() => setProductActionLogsDialogOpen(true)}
                    text={ActionLogsText}
                    disabled={false}
                  />
                  {permissions.edit_cameras && !autopatrolSite && (
                    <ThemeProvider theme={theme}>
                      <Button
                        type="button"
                        color="primary"
                        onClick={() => {
                          navigate(alertsLink);
                        }}
                        disabled={false}
                        className="hover:bg-[#E67300]"
                      >
                        <Link
                          href={alertsLink}
                          onClick={(e) => {
                            e.preventDefault(); // Prevent default anchor navigation
                            navigate(alertsLink);
                          }}
                          sx={{
                            textDecoration: "none",
                            color: "inherit",
                            cursor: "pointer",
                            height: "100%",
                          }}
                        >
                          {seeAlertText}
                        </Link>
                      </Button>
                    </ThemeProvider>
                  )}
                </div>
              </div>
            </div>
            <div className="mt-[15px] min-h-[450px] w-full min-w-[1250px] bg-white">
              <div className="flex">
                {allCamerasData.length > 1 && (
                  <div className="pt-[200px] w-[40px]">
                    <Tooltip
                      title={`Previous Camera: ${
                        cameraIndex === 0
                          ? allCamerasData[allCamerasData.length - 1]
                              .camera_name
                          : allCamerasData[cameraIndex - 1].camera_name
                      }`}
                      placement="top"
                      sx={{ fontSize: "12px", fontFamily: "mulish" }}
                    >
                      <div>
                        <OrangeTextButton
                          onClick={() => {
                            resetState(
                              setCameraDataLoaded,
                              setCameraObject,
                              setSiteInfo,
                              setClickedTab,
                              setEditMode,
                              setAddImmixAlertDialogOpen,
                              setAddSureviewAlertDialogOpen,
                              setCameraGeneralInfo,
                              setCameraGeneralInfoBackup,
                              setPatchObject,
                              setGeneralInfoChangesMade,
                              setActiveChangeMade,
                              setAxisCamera,
                              setMobotixCamera,
                              setActiveTab,
                              setBackdropLoadingScreenOn,
                              setImmixAlertsOnCamera,
                              setImmixAlerts,
                              setSureViewAlertsOnCamera,
                              setSureviewAlerts,
                              setIgnoreZonesBreadCrumbs,
                              setEditCameraAlerts,
                              setRecipientsConfiguredData,
                              setTitlesArray,
                              setActionRulesData,
                              setActionRulesTitlesArray,
                              setApplicationsConfiguredData,
                              setTimeTablesData,
                              setIPNotifyData,
                              setActionHandlerData,
                              setMobotixFinished,
                              setAxisFinished,
                              setStreamsObject,
                              setActiveProducts,
                              setSendingImmixTest,
                              setImmixTestSuccessDialogOpen,
                              setImmixTestFailDialogOpen,
                              setv1RedirectDialogOpen,
                              setSrcImages,
                              setCameraInfoRow,
                              setCameraWebhookData,
                              setCameraWebhooksPatchObject,
                              setCameraWebhooksChangesMade,
                              setProductsData,
                              setBackupProductsData,
                              setAllTabsOpen,
                              setAllCamerasData,
                              setCameraIndex,
                              setSentinelAlertsOnCamera,
                              setSentinelAlerts,
                              setBoldAlerts,
                              setBoldAlertsOnCamera,
                              setPatriotAlerts,
                              setPatriotAlertsOnCamera,
                              setUSMonitoringAlerts,
                              setUSMonitoringAlertsOnCamera,
                              setSoftguardAlerts,
                              setSoftguardAlertsOnCamera
                            );
                            setTimeout(() => {
                              if (cameraIndex === 0) {
                                navigate(
                                  `/sites/${getNumbersAfterSites(
                                    location.href
                                  )}/editcamera/${
                                    allCamerasData[allCamerasData.length - 1].id
                                  }`
                                );
                                localStorage.setItem(
                                  "editCamerActiveTab",
                                  "one"
                                );
                                localStorage.setItem(
                                  "currentCameraId",
                                  getNumbersAfterEditCamera(location.href)
                                );
                                getEditCameraData(
                                  navigate,
                                  getNumbersAfterSites(location.href),
                                  getNumbersAfterEditCamera(location.href),
                                  setSiteInfo,
                                  setCameraGeneralInfo,
                                  setCameraObject,
                                  setCameraDataLoaded,
                                  setBackdropLoadingScreenOn,
                                  setImmixAlertsOnCamera,
                                  setIgnoreZonesBreadCrumbs,
                                  createIgnoreZonesLinksArray,
                                  handleSitesBreadcrumbClick,
                                  handleSingularSiteBreadcrumbClick,
                                  setSureViewAlertsOnCamera,
                                  setAxisCamera,
                                  setMobotixFinished,
                                  setRecipientsConfiguredData,
                                  setTitlesArray,
                                  setActionRulesData,
                                  setActionRulesTitlesArray,
                                  setApplicationsConfiguredData,
                                  mobotixFinished,
                                  setAxisFinished,
                                  axisFinished,
                                  setMobotixCamera,
                                  setTimeTablesData,
                                  setIPNotifyData,
                                  setActionHandlerData,
                                  setCameraGeneralInfoBackup,
                                  setImmixAlerts,
                                  setSureviewAlerts,
                                  setActiveProducts,
                                  setActiveTab,
                                  actionRulesTitlesArray,
                                  applicationsConfiguredData,
                                  cameraGeneralInfo,
                                  actionRulesData,
                                  setSrcImages,
                                  permissions,
                                  setCameraInfoRow,
                                  width,
                                  setCameraWebhookData,
                                  setProductsData,
                                  setBackupProductsData,
                                  setSentinelAlertsOnCamera,
                                  setSentinelAlertsOnCamera,
                                  setBoldAlertsOnCamera,
                                  setBoldAlerts,
                                  setPatriotAlertsOnCamera,
                                  setPatriotAlerts,
                                  setUSMonitoringAlertsOnCamera,
                                  setUSMonitoringAlerts,
                                  setWebhooksAlertsOnCamera,
                                  setSoftguardAlertsOnCamera,
                                  setSoftguardAlerts,
                                  setEvalinkAlertsOnCamera,
                                  setStreamParametersData,
                                  setShowStreamParameters,
                                  setEditCameraAlerts,
                                  setDiceAlertsOnCamera,
                                  setDiceAlerts,
                                  undefined,
                                  isMountedRef
                                );
                                getAllCameras(
                                  Number(getNumbersAfterSites(location.href)),
                                  Number(
                                    getNumbersAfterEditCamera(location.href)
                                  ),
                                  setAllCamerasData,
                                  setCameraIndex
                                );
                              } else {
                                navigate(
                                  `/sites/${getNumbersAfterSites(
                                    location.href
                                  )}/editcamera/${
                                    allCamerasData[cameraIndex - 1].id
                                  }`
                                );
                                localStorage.setItem(
                                  "editCamerActiveTab",
                                  "one"
                                );
                                localStorage.setItem(
                                  "currentCameraId",
                                  getNumbersAfterEditCamera(location.href)
                                );
                                getEditCameraData(
                                  navigate,
                                  getNumbersAfterSites(location.href),
                                  getNumbersAfterEditCamera(location.href),
                                  setSiteInfo,
                                  setCameraGeneralInfo,
                                  setCameraObject,
                                  setCameraDataLoaded,
                                  setBackdropLoadingScreenOn,
                                  setImmixAlertsOnCamera,
                                  setIgnoreZonesBreadCrumbs,
                                  createIgnoreZonesLinksArray,
                                  handleSitesBreadcrumbClick,
                                  handleSingularSiteBreadcrumbClick,
                                  setSureViewAlertsOnCamera,
                                  setAxisCamera,
                                  setMobotixFinished,
                                  setRecipientsConfiguredData,
                                  setTitlesArray,
                                  setActionRulesData,
                                  setActionRulesTitlesArray,
                                  setApplicationsConfiguredData,
                                  mobotixFinished,
                                  setAxisFinished,
                                  axisFinished,
                                  setMobotixCamera,
                                  setTimeTablesData,
                                  setIPNotifyData,
                                  setActionHandlerData,
                                  setCameraGeneralInfoBackup,
                                  setImmixAlerts,
                                  setSureviewAlerts,
                                  setActiveProducts,
                                  setActiveTab,
                                  actionRulesTitlesArray,
                                  applicationsConfiguredData,
                                  cameraGeneralInfo,
                                  actionRulesData,
                                  setSrcImages,
                                  permissions,
                                  setCameraInfoRow,
                                  width,
                                  setCameraWebhookData,
                                  setProductsData,
                                  setBackupProductsData,
                                  setSentinelAlertsOnCamera,
                                  setSentinelAlerts,
                                  setBoldAlertsOnCamera,
                                  setBoldAlerts,
                                  setPatriotAlertsOnCamera,
                                  setPatriotAlerts,
                                  setUSMonitoringAlertsOnCamera,
                                  setUSMonitoringAlerts,
                                  setWebhooksAlertsOnCamera,
                                  setSoftguardAlertsOnCamera,
                                  setSoftguardAlerts,
                                  setEvalinkAlertsOnCamera,
                                  setStreamParametersData,
                                  setShowStreamParameters,
                                  setEditCameraAlerts,
                                  setDiceAlertsOnCamera,
                                  setDiceAlerts,
                                  undefined,
                                  isMountedRef
                                );
                                getAllCameras(
                                  Number(getNumbersAfterSites(location.href)),
                                  Number(
                                    getNumbersAfterEditCamera(location.href)
                                  ),
                                  setAllCamerasData,
                                  setCameraIndex
                                );
                              }
                            }, 1);
                          }}
                          text={
                            <ArrowBackIosIcon
                              fontSize="large"
                              id="backButton"
                            />
                          }
                          disabled={allCamerasData.length < 2}
                        />
                      </div>
                    </Tooltip>
                  </div>
                )}

                <div>
                  <Table
                    sx={{ minWidth: 1000, fontFamily: "Mulish" }}
                    aria-label="simple table"
                  >
                    <TableBody>{cameraInfoRow}</TableBody>
                  </Table>
                </div>
                {allCamerasData.length > 1 && (
                  <div className="pt-[200px] pl-[280px]">
                    <Tooltip
                      title={`Next Camera: ${
                        cameraIndex + 1 === allCamerasData.length
                          ? allCamerasData[0].camera_name
                          : allCamerasData[cameraIndex + 1].camera_name
                      }`}
                      placement="top"
                      sx={{ fontSize: "12px", fontFamily: "mulish" }}
                    >
                      <div>
                        <OrangeTextButton
                          onClick={() => {
                            resetState(
                              setCameraDataLoaded,
                              setCameraObject,
                              setSiteInfo,
                              setClickedTab,
                              setEditMode,
                              setAddImmixAlertDialogOpen,
                              setAddSureviewAlertDialogOpen,
                              setCameraGeneralInfo,
                              setCameraGeneralInfoBackup,
                              setPatchObject,
                              setGeneralInfoChangesMade,
                              setActiveChangeMade,
                              setAxisCamera,
                              setMobotixCamera,
                              setActiveTab,
                              setBackdropLoadingScreenOn,
                              setImmixAlertsOnCamera,
                              setImmixAlerts,
                              setSureViewAlertsOnCamera,
                              setSureviewAlerts,
                              setIgnoreZonesBreadCrumbs,
                              setEditCameraAlerts,
                              setRecipientsConfiguredData,
                              setTitlesArray,
                              setActionRulesData,
                              setActionRulesTitlesArray,
                              setApplicationsConfiguredData,
                              setTimeTablesData,
                              setIPNotifyData,
                              setActionHandlerData,
                              setMobotixFinished,
                              setAxisFinished,
                              setStreamsObject,
                              setActiveProducts,
                              setSendingImmixTest,
                              setImmixTestSuccessDialogOpen,
                              setImmixTestFailDialogOpen,
                              setv1RedirectDialogOpen,
                              setSrcImages,
                              setCameraInfoRow,
                              setCameraWebhookData,
                              setCameraWebhooksPatchObject,
                              setCameraWebhooksChangesMade,
                              setProductsData,
                              setBackupProductsData,
                              setAllTabsOpen,
                              setAllCamerasData,
                              setCameraIndex,
                              setSentinelAlertsOnCamera,
                              setSentinelAlerts,
                              setBoldAlerts,
                              setBoldAlertsOnCamera,
                              setPatriotAlertsOnCamera,
                              setPatriotAlerts,
                              setUSMonitoringAlertsOnCamera,
                              setUSMonitoringAlerts,
                              setSoftguardAlertsOnCamera,
                              setSoftguardAlerts
                            );
                            setTimeout(() => {
                              if (cameraIndex + 1 === allCamerasData.length) {
                                navigate(
                                  `/sites/${getNumbersAfterSites(
                                    location.href
                                  )}/editcamera/${allCamerasData[0].id}`
                                );
                                // ENG-2680: like Previous, point localStorage at
                                // the camera now open (the save used to hit the
                                // previous camera after Next).
                                recordEditCameraOpened();
                                getEditCameraData(
                                  navigate,
                                  getNumbersAfterSites(location.href),
                                  getNumbersAfterEditCamera(location.href),
                                  setSiteInfo,
                                  setCameraGeneralInfo,
                                  setCameraObject,
                                  setCameraDataLoaded,
                                  setBackdropLoadingScreenOn,
                                  setImmixAlertsOnCamera,
                                  setIgnoreZonesBreadCrumbs,
                                  createIgnoreZonesLinksArray,
                                  handleSitesBreadcrumbClick,
                                  handleSingularSiteBreadcrumbClick,
                                  setSureViewAlertsOnCamera,
                                  setAxisCamera,
                                  setMobotixFinished,
                                  setRecipientsConfiguredData,
                                  setTitlesArray,
                                  setActionRulesData,
                                  setActionRulesTitlesArray,
                                  setApplicationsConfiguredData,
                                  mobotixFinished,
                                  setAxisFinished,
                                  axisFinished,
                                  setMobotixCamera,
                                  setTimeTablesData,
                                  setIPNotifyData,
                                  setActionHandlerData,
                                  setCameraGeneralInfoBackup,
                                  setImmixAlerts,
                                  setSureviewAlerts,
                                  setActiveProducts,
                                  setActiveTab,
                                  actionRulesTitlesArray,
                                  applicationsConfiguredData,
                                  cameraGeneralInfo,
                                  actionRulesData,
                                  setSrcImages,
                                  permissions,
                                  setCameraInfoRow,
                                  width,
                                  setCameraWebhookData,
                                  setProductsData,
                                  setBackupProductsData,
                                  setSentinelAlertsOnCamera,
                                  setSentinelAlerts,
                                  setBoldAlertsOnCamera,
                                  setBoldAlerts,
                                  setPatriotAlertsOnCamera,
                                  setPatriotAlerts,
                                  setUSMonitoringAlertsOnCamera,
                                  setUSMonitoringAlerts,
                                  setWebhooksAlertsOnCamera,
                                  setSoftguardAlertsOnCamera,
                                  setSoftguardAlerts,
                                  setEvalinkAlertsOnCamera,
                                  setStreamParametersData,
                                  setShowStreamParameters,
                                  setEditCameraAlerts,
                                  setDiceAlertsOnCamera,
                                  setDiceAlerts,
                                  undefined,
                                  isMountedRef
                                );
                                getAllCameras(
                                  Number(getNumbersAfterSites(location.href)),
                                  Number(
                                    getNumbersAfterEditCamera(location.href)
                                  ),
                                  setAllCamerasData,
                                  setCameraIndex
                                );
                              } else {
                                navigate(
                                  `/sites/${getNumbersAfterSites(
                                    location.href
                                  )}/editcamera/${
                                    allCamerasData[cameraIndex + 1].id
                                  }`
                                );
                                // ENG-2680: like Previous, point localStorage at
                                // the camera now open (the save used to hit the
                                // previous camera after Next).
                                recordEditCameraOpened();
                                getEditCameraData(
                                  navigate,
                                  getNumbersAfterSites(location.href),
                                  getNumbersAfterEditCamera(location.href),
                                  setSiteInfo,
                                  setCameraGeneralInfo,
                                  setCameraObject,
                                  setCameraDataLoaded,
                                  setBackdropLoadingScreenOn,
                                  setImmixAlertsOnCamera,
                                  setIgnoreZonesBreadCrumbs,
                                  createIgnoreZonesLinksArray,
                                  handleSitesBreadcrumbClick,
                                  handleSingularSiteBreadcrumbClick,
                                  setSureViewAlertsOnCamera,
                                  setAxisCamera,
                                  setMobotixFinished,
                                  setRecipientsConfiguredData,
                                  setTitlesArray,
                                  setActionRulesData,
                                  setActionRulesTitlesArray,
                                  setApplicationsConfiguredData,
                                  mobotixFinished,
                                  setAxisFinished,
                                  axisFinished,
                                  setMobotixCamera,
                                  setTimeTablesData,
                                  setIPNotifyData,
                                  setActionHandlerData,
                                  setCameraGeneralInfoBackup,
                                  setImmixAlerts,
                                  setSureviewAlerts,
                                  setActiveProducts,
                                  setActiveTab,
                                  actionRulesTitlesArray,
                                  applicationsConfiguredData,
                                  cameraGeneralInfo,
                                  actionRulesData,
                                  setSrcImages,
                                  permissions,
                                  setCameraInfoRow,
                                  width,
                                  setCameraWebhookData,
                                  setProductsData,
                                  setBackupProductsData,
                                  setSentinelAlertsOnCamera,
                                  setSentinelAlerts,
                                  setBoldAlertsOnCamera,
                                  setBoldAlerts,
                                  setPatriotAlertsOnCamera,
                                  setPatriotAlerts,
                                  setUSMonitoringAlertsOnCamera,
                                  setUSMonitoringAlerts,
                                  setWebhooksAlertsOnCamera,
                                  setSoftguardAlertsOnCamera,
                                  setSoftguardAlerts,
                                  setEvalinkAlertsOnCamera,
                                  setStreamParametersData,
                                  setShowStreamParameters,
                                  setEditCameraAlerts,
                                  setDiceAlertsOnCamera,
                                  setDiceAlerts,
                                  undefined,
                                  isMountedRef
                                );
                                getAllCameras(
                                  Number(getNumbersAfterSites(location.href)),
                                  Number(
                                    getNumbersAfterEditCamera(location.href)
                                  ),
                                  setAllCamerasData,
                                  setCameraIndex
                                );
                              }
                            }, 1);
                          }}
                          text={
                            <ArrowForwardIosIcon
                              fontSize="large"
                              id="forwardButton"
                            />
                          }
                          disabled={allCamerasData.length < 2}
                        />
                      </div>
                    </Tooltip>
                  </div>
                )}
              </div>
            </div>
            <div className="relative mt-[15px] h-[59px] w-full min-w-[1250px] bg-white">
              <div className="flex">
                <p className="font-black font-mulish text-[20px] p-3 indent-4">
                  Camera Information
                </p>
              </div>
              <div className="absolute right-[90px] top-[10px]">
                {!editMode &&
                  clickedTab !== "seven" &&
                  clickedTab !== "nine" &&
                  permissions.edit_cameras && (
                    <OrangeTextButton
                      onClick={() => {
                        handleEditInfoButtonClick(
                          setEditMode,
                          clickedTab,
                          setActiveTab,
                          siteInfo,
                          cameraGeneralInfo,
                          setCameraGeneralInfo,
                          setPatchObject,
                          setGeneralInfoChangesMade,
                          setActiveChangeMade,
                          immixAlerts,
                          diceAlerts,
                          sureviewAlerts,
                          activeProducts,
                          cameraWebhookData,
                          productsData,
                          setProductsData,
                          allTabsOpen,
                          setAllTabsOpen,
                          setBackupProductsData,
                          sentinelAlerts,
                          boldAlerts,
                          patriotAlerts,
                          softguardAlerts,
                          streamParametersData,
                          streamParametersPatchObject,
                          setStreamParametersPatchObject,
                          setStreamParametersChangesMade,
                          setEditStreamWebhookDialogOpen,
                          immixAlertsOnCamera,
                          diceAlertsOnCamera,
                          sureViewAlertsOnCamera,
                          webhooksAlertsOnCamera,
                          sentinelAlertsOnCamera,
                          boldAlertsOnCamera,
                          patriotAlertsOnCamera,
                          softguardAlertsOnCamera,
                          evalinkAlertsOnCamera,
                          setAddStreamWebhookDialogOpen
                        );
                      }}
                      text={editInformationText}
                      disabled={false}
                    />
                  )}
              </div>
              <div className={"absolute top-[10px] right-[90px]"}>
                {editMode && clickedTab !== "seven" && (
                  <EditCameraSaveChangesButton
                    setEditMode={setEditMode}
                    clickedTab={clickedTab}
                    setActiveTab={setActiveTab}
                    siteInfo={siteInfo}
                    cameraGeneralInfo={cameraGeneralInfo}
                    setBackdropLoadingScreenOn={setBackdropLoadingScreenOn}
                    generalInfoChangesMade={generalInfoChangesMade}
                    patchObject={patchObject}
                    setGeneralInfoChangesMade={setGeneralInfoChangesMade}
                    setPatchObject={setPatchObject}
                    setCameraGeneralInfo={setCameraGeneralInfo}
                    cameraGeneralInfoBackup={cameraGeneralInfoBackup}
                    setCameraGeneralInfoBackup={setCameraGeneralInfoBackup}
                    immixAlerts={immixAlerts}
                    diceAlerts={diceAlerts}
                    activeChangeMade={activeChangeMade}
                    sureviewAlerts={sureviewAlerts}
                    setIgnoreZonesBreadCrumbs={setIgnoreZonesBreadCrumbs}
                    handleSitesBreadcrumbClick={handleSitesBreadcrumbClick}
                    handleSingularSiteBreadcrumbClick={
                      handleSingularSiteBreadcrumbClick
                    }
                    setAddImmixAlertDialogOpen={setAddImmixAlertDialogOpen}
                    setAddDiceAlertDialogOpen={setAddDiceAlertDialogOpen}
                    setAddSureviewAlertDialogOpen={
                      setAddSureviewAlertDialogOpen
                    }
                    activeProducts={activeProducts}
                    setStreamsObject={setStreamsObject}
                    setActiveProducts={setActiveProducts}
                    setImmixAlertsOnCamera={setImmixAlertsOnCamera}
                    setDiceAlertsOnCamera={setDiceAlertsOnCamera}
                    setSureViewAlertsOnCamera={setSureViewAlertsOnCamera}
                    setSendingImmixTest={setSendingImmixTest}
                    setImmixTestSuccessDialogOpen={
                      setImmixTestSuccessDialogOpen
                    }
                    setImmixTestFailDialogOpen={setImmixTestFailDialogOpen}
                    setSendingDiceTest={setSendingDiceTest}
                    setDiceTestSuccessDialogOpen={setDiceTestSuccessDialogOpen}
                    setDiceTestFailDialogOpen={setDiceTestFailDialogOpen}
                    setSrcImages={setSrcImages}
                    setCameraInfoRow={setCameraInfoRow}
                    width={width}
                    permissions={permissions}
                    cameraWebhooksChangesMade={cameraWebhooksChangesMade}
                    cameraWebhooksPatchObject={cameraWebhooksPatchObject}
                    recipientsConfiguredData={recipientsConfiguredData}
                    titlesArray={titlesArray}
                    setRecipientsConfiguredData={setRecipientsConfiguredData}
                    setTitlesArray={setTitlesArray}
                    actionRulesData={actionRulesData}
                    setActionRulesData={setActionRulesData}
                    actionRulesTitlesArray={actionRulesTitlesArray}
                    setActionRulesTitlesArray={setActionRulesTitlesArray}
                    applicationsConfiguredData={applicationsConfiguredData}
                    axisFinished={axisFinished}
                    timeTablesData={timeTablesData}
                    setTimeTablesData={setTimeTablesData}
                    iPNotifyData={iPNotifyData}
                    setIPNotifyData={setIPNotifyData}
                    actionHandlerData={actionHandlerData}
                    setActionHandlerData={setActionHandlerData}
                    cameraWebhookData={cameraWebhookData}
                    setCameraWebhookData={setCameraWebhookData}
                    setCameraWebhooksPatchObject={setCameraWebhooksPatchObject}
                    setCameraWebhooksChangesMade={setCameraWebhooksChangesMade}
                    productsData={productsData}
                    setProductsData={setProductsData}
                    allTabsOpen={allTabsOpen}
                    setAllTabsOpen={setAllTabsOpen}
                    backupProductsData={backupProductsData}
                    setBackupProductsData={setBackupProductsData}
                    setSentinelAlertsOnCamera={setSentinelAlertsOnCamera}
                    sentinelAlerts={sentinelAlerts}
                    setAddSentinelAlertDialogOpen={
                      setAddSentinelAlertDialogOpen
                    }
                    setBoldAlertsOnCamera={setBoldAlertsOnCamera}
                    boldAlerts={boldAlerts}
                    setAddBoldAlertDialogOpen={setAddBoldAlertDialogOpen}
                    setPatriotAlertsOnCamera={setPatriotAlertsOnCamera}
                    patriotAlerts={patriotAlerts}
                    setPatriotAlerts={setPatriotAlerts}
                    setAddPatriotAlertDialogOpen={setAddPatriotAlertDialogOpen}
                    setUSMonitoringAlertsOnCamera={
                      setUSMonitoringAlertsOnCamera
                    }
                    uSMonitoringAlerts={uSMonitoringAlerts}
                    setAddUSMonitoringAlertDialogOpen={
                      setAddUSMonitoringAlertDialogOpen
                    }
                    setSoftguardAlertsOnCamera={setSoftguardAlertsOnCamera}
                    softguardAlerts={softguardAlerts}
                    setAddSoftguardAlertDialogOpen={
                      setAddSoftguardAlertDialogOpen
                    }
                    setSoftguardAlerts={setSoftguardAlerts}
                    streamParametersData={streamParametersData}
                    streamParametersChangesMade={streamParametersChangesMade}
                    setStreamParametersData={setStreamParametersData}
                    setStreamParametersChangesMade={
                      setStreamParametersChangesMade
                    }
                    streamParametersPatchObject={streamParametersPatchObject}
                    setAddStreamWebhookDialogOpen={
                      setAddStreamWebhookDialogOpen
                    }
                    setEditStreamWebhookDialogOpen={
                      setEditStreamWebhookDialogOpen
                    }
                    setDeleteImmixAlertDialogOpen={
                      setDeleteImmixAlertDialogOpen
                    }
                    setDeleteDiceAlertDialogOpen={setDeleteDiceAlertDialogOpen}
                    immixAlertsOnCamera={immixAlertsOnCamera}
                    diceAlertsOnCamera={diceAlertsOnCamera}
                    sureViewAlertsOnCamera={sureViewAlertsOnCamera}
                    webhooksAlertsOnCamera={webhooksAlertsOnCamera}
                    sentinelAlertsOnCamera={sentinelAlertsOnCamera}
                    boldAlertsOnCamera={boldAlertsOnCamera}
                    patriotAlertsOnCamera={patriotAlertsOnCamera}
                    softguardAlertsOnCamera={softguardAlertsOnCamera}
                    evalinkAlertsOnCamera={evalinkAlertsOnCamera}
                    setEvalinkAlertsOnCamera={setEvalinkAlertsOnCamera}
                    setCameraObject={setCameraObject}
                    saveProgress={{
                      setProgressPercent: setEditPageLoadProgressPercent,
                      setProgressMessage: setEditPageLoadProgressMessage,
                    }}
                  />
                )}
              </div>
              <div className={"absolute top-[10px] right-[250px]"}>
                {editMode && clickedTab !== "seven" && (
                  <EditCameraCancelButton
                    setEditMode={setEditMode}
                    clickedTab={clickedTab}
                    setActiveTab={setActiveTab}
                    siteInfo={siteInfo}
                    cameraGeneralInfo={cameraGeneralInfo}
                    setCameraGeneralInfo={setCameraGeneralInfo}
                    cameraGeneralInfoBackup={cameraGeneralInfoBackup}
                    setPatchObject={setPatchObject}
                    immixAlerts={immixAlerts}
                    diceAlerts={diceAlerts}
                    sureviewAlerts={sureviewAlerts}
                    setAddImmixAlertDialogOpen={setAddImmixAlertDialogOpen}
                    setAddDiceAlertDialogOpen={setAddDiceAlertDialogOpen}
                    setAddSureviewAlertDialogOpen={
                      setAddSureviewAlertDialogOpen
                    }
                    activeProducts={activeProducts}
                    setBackdropLoadingScreenOn={setBackdropLoadingScreenOn}
                    setSendingImmixTest={setSendingImmixTest}
                    setImmixTestSuccessDialogOpen={
                      setImmixTestSuccessDialogOpen
                    }
                    setImmixTestFailDialogOpen={setImmixTestFailDialogOpen}
                    setSendingDiceTest={setSendingDiceTest}
                    setDiceTestSuccessDialogOpen={setDiceTestSuccessDialogOpen}
                    setDiceTestFailDialogOpen={setDiceTestFailDialogOpen}
                    recipientsConfiguredData={recipientsConfiguredData}
                    titlesArray={titlesArray}
                    setRecipientsConfiguredData={setRecipientsConfiguredData}
                    setTitlesArray={setTitlesArray}
                    actionRulesData={actionRulesData}
                    setActionRulesData={setActionRulesData}
                    actionRulesTitlesArray={actionRulesTitlesArray}
                    setActionRulesTitlesArray={setActionRulesTitlesArray}
                    applicationsConfiguredData={applicationsConfiguredData}
                    axisFinished={axisFinished}
                    timeTablesData={timeTablesData}
                    setTimeTablesData={setTimeTablesData}
                    iPNotifyData={iPNotifyData}
                    setIPNotifyData={setIPNotifyData}
                    actionHandlerData={actionHandlerData}
                    setActionHandlerData={setActionHandlerData}
                    cameraWebhookData={cameraWebhookData}
                    productsData={productsData}
                    setProductsData={setProductsData}
                    allTabsOpen={allTabsOpen}
                    setAllTabsOpen={setAllTabsOpen}
                    backupProductsData={backupProductsData}
                    setBackupProductsData={setBackupProductsData}
                    sentinelAlerts={sentinelAlerts}
                    setAddSentinelAlertDialogOpen={
                      setAddSentinelAlertDialogOpen
                    }
                    boldAlerts={boldAlerts}
                    setAddBoldAlertDialogOpen={setAddBoldAlertDialogOpen}
                    patriotAlerts={patriotAlerts}
                    setAddPatriotAlertDialogOpen={setAddPatriotAlertDialogOpen}
                    uSMonitoringAlerts={uSMonitoringAlerts}
                    setAddUSMonitoringAlertDialogOpen={
                      setAddUSMonitoringAlertDialogOpen
                    }
                    softguardAlerts={softguardAlerts}
                    setAddSoftguardAlertDialogOpen={
                      setAddSoftguardAlertDialogOpen
                    }
                    streamParametersData={streamParametersData}
                    setAddStreamWebhookDialogOpen={
                      setAddStreamWebhookDialogOpen
                    }
                    setEditStreamWebhookDialogOpen={
                      setEditStreamWebhookDialogOpen
                    }
                    setDeleteImmixAlertDialogOpen={
                      setDeleteImmixAlertDialogOpen
                    }
                    setDeleteDiceAlertDialogOpen={setDeleteDiceAlertDialogOpen}
                    immixAlertsOnCamera={immixAlertsOnCamera}
                    diceAlertsOnCamera={diceAlertsOnCamera}
                    sureViewAlertsOnCamera={sureViewAlertsOnCamera}
                    webhooksAlertsOnCamera={webhooksAlertsOnCamera}
                    sentinelAlertsOnCamera={sentinelAlertsOnCamera}
                    boldAlertsOnCamera={boldAlertsOnCamera}
                    patriotAlertsOnCamera={patriotAlertsOnCamera}
                    softguardAlertsOnCamera={softguardAlertsOnCamera}
                    evalinkAlertsOnCamera={evalinkAlertsOnCamera}
                    navigate={navigate}
                  />
                )}
              </div>
            </div>
            <div className="w-full min-w-[1250px] bg-white">
              <EditCameraTabs
                clickedTab={clickedTab}
                setClickedTab={setClickedTab}
                editMode={editMode}
                setActiveTab={setActiveTab}
                siteInfo={siteInfo}
                cameraGeneralInfo={cameraGeneralInfo}
                setCameraGeneralInfo={setCameraGeneralInfo}
                setPatchObject={setPatchObject}
                setGeneralInfoChangesMade={setGeneralInfoChangesMade}
                immixAlertsOnCamera={immixAlertsOnCamera}
                diceAlertsOnCamera={diceAlertsOnCamera}
                sureViewAlertsOnCamera={sureViewAlertsOnCamera}
                immixAlerts={immixAlerts}
                diceAlerts={diceAlerts}
                setActiveChangeMade={setActiveChangeMade}
                sureviewAlerts={sureviewAlerts}
                setAddImmixAlertDialogOpen={setAddImmixAlertDialogOpen}
                setAddDiceAlertDialogOpen={setAddDiceAlertDialogOpen}
                setAddSureviewAlertDialogOpen={setAddSureviewAlertDialogOpen}
                streamsObject={streamsObject}
                setActiveProducts={setActiveProducts}
                activeProducts={activeProducts}
                axisCamera={axisCamera}
                recipientsConfiguredData={recipientsConfiguredData}
                titlesArray={titlesArray}
                navigate={navigate}
                setBackdropLoadingScreenOn={setBackdropLoadingScreenOn}
                setRecipientsConfiguredData={setRecipientsConfiguredData}
                setTitlesArray={setTitlesArray}
                actionRulesData={actionRulesData}
                setActionRulesData={setActionRulesData}
                actionRulesTitlesArray={actionRulesTitlesArray}
                setActionRulesTitlesArray={setActionRulesTitlesArray}
                applicationsConfiguredData={applicationsConfiguredData}
                mobotixCamera={mobotixCamera}
                timeTablesData={timeTablesData}
                setTimeTablesData={setTimeTablesData}
                iPNotifyData={iPNotifyData}
                setIPNotifyData={setIPNotifyData}
                actionHandlerData={actionHandlerData}
                setActionHandlerData={setActionHandlerData}
                axisFinished={axisFinished}
                setSendingImmixTest={setSendingImmixTest}
                setImmixTestSuccessDialogOpen={setImmixTestSuccessDialogOpen}
                setImmixTestFailDialogOpen={setImmixTestFailDialogOpen}
                setSendingDiceTest={setSendingDiceTest}
                setDiceTestSuccessDialogOpen={setDiceTestSuccessDialogOpen}
                setDiceTestFailDialogOpen={setDiceTestFailDialogOpen}
                cameraWebhookData={cameraWebhookData}
                setCameraWebhooksPatchObject={setCameraWebhooksPatchObject}
                setCameraWebhooksChangesMade={setCameraWebhooksChangesMade}
                productsData={productsData}
                setProductsData={setProductsData}
                allTabsOpen={allTabsOpen}
                setAllTabsOpen={setAllTabsOpen}
                setBackupProductsData={setBackupProductsData}
                sentinelAlertsOnCamera={sentinelAlertsOnCamera}
                sentinelAlerts={sentinelAlerts}
                setAddSentinelAlertDialogOpen={setAddSentinelAlertDialogOpen}
                boldAlertsOnCamera={boldAlertsOnCamera}
                boldAlerts={boldAlerts}
                setAddBoldAlertDialogOpen={setAddBoldAlertDialogOpen}
                patriotAlertsOnCamera={patriotAlertsOnCamera}
                patriotAlerts={patriotAlerts}
                setAddPatriotAlertDialogOpen={setAddPatriotAlertDialogOpen}
                uSMonitoringAlertsOnCamera={uSMonitoringAlertsOnCamera}
                uSMonitoringAlerts={uSMonitoringAlerts}
                setAddUSMonitoringAlertDialogOpen={
                  setAddUSMonitoringAlertDialogOpen
                }
                webhooksAlertsOnCamera={webhooksAlertsOnCamera}
                softguardAlertsOnCamera={softguardAlertsOnCamera}
                softguardAlerts={softguardAlerts}
                setAddSoftguardAlertDialogOpen={setAddSoftguardAlertDialogOpen}
                evalinkAlertsOnCamera={evalinkAlertsOnCamera}
                showStreamParameters={showStreamParameters}
                streamParametersData={streamParametersData}
                streamParametersPatchObject={streamParametersPatchObject}
                setStreamParametersPatchObject={setStreamParametersPatchObject}
                setStreamParametersChangesMade={setStreamParametersChangesMade}
                setAddStreamWebhookDialogOpen={setAddStreamWebhookDialogOpen}
                setEditStreamWebhookDialogOpen={setEditStreamWebhookDialogOpen}
                setDeleteImmixAlertDialogOpen={setDeleteImmixAlertDialogOpen}
                setDeleteDiceAlertDialogOpen={setDeleteDiceAlertDialogOpen}
              />
            </div>
            <div className="min-h-[17vh] w-full min-w-[1250px]">
              {activeTab}
            </div>
            </div>
          </div>
        )}
      </div>
      <div className="justify-start">
        <NavBar
          navBarCollapsed={navBarCollapsed}
          setNavBarCollapsed={setNavBarCollapsed}
          props={{}}
        />
      </div>
    </div>
  );
};
